/** @format */

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { FormikProps, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import StepperButtons from '../../../../common/custom/StepperButtons';
import { selectConstants } from '../../../../common/dropdowns/Api/constantSlice';
import { DropdownDto } from '../../../../common/dropdowns/Api/Models';
import Provinces from '../../../../common/dropdowns/Provinces';
import Universities from '../../../../common/dropdowns/Universities';
import YearOfStudy from '../../../../common/dropdowns/YearOfStudt';
import { addEducationDetailsThunk, selectApplicant } from '../../ApplicantSlice';
import { InstitutionDto } from '../../Models';
import SavedApplicationPopUp from '../SavedApplicationPopUp';

interface Props {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  activeStep: number;
}

/** Next calendar year: intake assumed for gap-year and still-in-HS matric rule. Matches backend. */
function nextBursaryIntakeCalendarYear(): number {
  return new Date().getFullYear() + 1;
}

function effectiveQualificationStartYearForGap(values: InstitutionDto): number {
  if (values.isTakingGapYear === true) {
    return nextBursaryIntakeCalendarYear();
  }
  if (values.yearOfStudyId === 1) {
    return Number(values.qualificationStartYear);
  }
  if (values.isInHighSchool === true) {
    return nextBursaryIntakeCalendarYear();
  }
  return Number(values.qualificationStartYear);
}

function educationMatricGapRuleApplies(values: InstitutionDto): boolean {
  if (values.isInHighSchool === true) return true;
  return values.yearOfStudyId === 1 || values.isTakingGapYear === true;
}

/** Match StudyDetailsService: 0–2 gap years between matric and effective first-year start year. */
function educationMatricGapIsEligible(values: InstitutionDto): boolean {
  const matric = parseInt(values.matriculationYear?.toString() ?? '', 10);
  if (Number.isNaN(matric)) return false;
  const qual = effectiveQualificationStartYearForGap(values);
  if (!Number.isFinite(qual) || qual <= 0) return false;
  const gapYears = qual - matric - 1;
  return gapYears >= 0 && gapYears <= 2;
}

/** Coerce saved/API subject ids so checkbox matching stays consistent (number vs string from JSON). */
function normalizeSubjectIds(ids: unknown): number[] {
  if (!Array.isArray(ids)) return [];
  const nums = ids
    .map((x) =>
      typeof x === 'string' ? parseInt(x, 10) : Number(x)
    )
    .filter((n) => Number.isFinite(n) && n > 0);
  const unique: number[] = [];
  for (let i = 0; i < nums.length; i++) {
    const n = nums[i];
    if (!unique.includes(n)) {
      unique.push(n);
    }
  }
  return unique;
}

export default function Education({ setActiveStep, activeStep }: Props) {
  const selData = useAppSelector(selectConstants);
  const applicant = useAppSelector(selectApplicant);
  const dispatch = useAppDispatch();
  const [isSaveAndClose, setIsSaveAndClose] = useState<boolean>(false);
  const validationSchema = yup.object({
    isInHighSchool: yup.boolean(),
    provinceId: yup.number().required('Province is required').min(1),
    highSchoolName: yup.string().required('High school name is required'),
    matriculationYear: yup
      .string()
      .required('Matriculation year is required')
      .matches(/^[0-9]+$/, 'Matriculation year should contain only digits')
      .min(4, 'Matriculation year should should be four digits')
      .test(
        'matriculationYear',
        'The matriculation year cannot be greater than current year',
        (value: string) => {
          const year = parseInt(value);
          const currentYear = new Date().getFullYear();
          if (year > currentYear) return false;
          else return true;
        }
      )
      .test(
        'matric-before-qualification-start-matric-field',
        'Matriculation year must be before your qualification start year at university.',
        function (value) {
          const { isInHighSchool, qualificationStartYear } = this.parent;
          if (isInHighSchool === true) return true;
          const q = Number(qualificationStartYear);
          const matric = parseInt(String(value ?? ''), 10);
          if (!q || q <= 0 || Number.isNaN(matric)) return true;
          return matric < q;
        }
      ),
    subjectIds: yup
      .array()
      .required()
      .min(1, 'Please select at least one subject'),
    institutionId: yup.number().when(['isInHighSchool', 'isTakingGapYear'], {
      is: (inHs: boolean, gap: boolean) => inHs === false && gap !== true,
      then: (schema) =>
        schema
          .required('Institution is required')
          .min(1, 'Institution is required'),
      otherwise: (schema) => schema.min(0),
    }),
    yearOfStudyId: yup.number().when('isInHighSchool', {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) =>
        schema.when('isTakingGapYear', {
          is: true,
          then: (s) =>
            s
              .required('Year of study is required')
              .oneOf(
                [1],
                'Gap year applicants must be applying for first year of tertiary study.'
              ),
          otherwise: (s) =>
            s
              .required('Year of study is required')
              .min(1, 'Year of study is required'),
        }),
    }),
    qualificationStartYear: yup.number().when('isInHighSchool', {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) =>
        schema.when('isTakingGapYear', {
          is: true,
          then: (s) => s.optional(),
          otherwise: (s) =>
            s
              .required('Qualification start year is required')
              .min(1, 'Qualification start year is required')
              .test(
                'matric-before-qualification-start',
                'Matriculation year must be before your qualification start year at university.',
                function (value) {
                  const matricRaw = this.parent.matriculationYear;
                  const matric = parseInt(String(matricRaw ?? ''), 10);
                  if (!value || value <= 0 || Number.isNaN(matric)) return true;
                  return matric < value;
                }
              ),
        }),
    }),
  });
  const [formValues, setFormValues] = useState<InstitutionDto>();
  const [qualificationList, setQualificationList] = useState<number[]>([]);
  const [isSaveAndCloseMessage, setIsSaveAndCloseMessage] =
    useState<string>('');
  const [isPrefilledFromPreviousYear, setIsPrefilledFromPreviousYear] =
    useState<boolean>(false);
  const [matricGapBlockOpen, setMatricGapBlockOpen] = useState(false);

  const initialValues = {
    institutionTypeId: 0,
    provinceId: 0,
    highSchoolName: '',
    institutionId: 0,
    matriculationYear: '',
    subjectIds: [],
    isInHighSchool: true,
    isTakingGapYear: false,
    qualificationStartYear: 0,
    yearOfStudyId: 0,
  };
  const formik: FormikProps<InstitutionDto> = useFormik<InstitutionDto>({
    initialValues: formValues || initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values: InstitutionDto) => {
      const payload: InstitutionDto = {
        ...values,
        subjectIds: normalizeSubjectIds(values.subjectIds),
        ...(values.isTakingGapYear === true
          ? { qualificationStartYear: nextBursaryIntakeCalendarYear() }
          : {}),
      };
      if (
        educationMatricGapRuleApplies(payload) &&
        !educationMatricGapIsEligible(payload)
      ) {
        setMatricGapBlockOpen(true);
        return;
      }
      // Always hit the backend. The service replaces the Education row in place
      // on re-save, so repeat calls are safe, and returning applicants with
      // pre-filled data from last year MUST submit on first confirm so a new
      // Education row is created for the current yearly application.
      dispatch(addEducationDetailsThunk(payload)).then((resp: any) => {
        if (
          resp.type.includes('fulfilled') &&
          (resp.payload?.validation?.code === 201 ||
            resp.payload?.validation?.code === 200)
        ) {
          setIsPrefilledFromPreviousYear(false);
          if (isSaveAndClose) {
            setIsSaveAndCloseMessage(
              'Your application was saved successfully, you can come back later to complete your application. Press close application to log out and keep your information secure'
            );
          } else {
            setActiveStep(activeStep + 1);
          }
        }
      });
    },
  });

  const loadEducation = () => {
    const progress = applicant.loginState.accessToken;
    if (progress && progress.application && progress.application.education) {
      let data = progress.application.education;
      const gap = data.isTakingGapYear === true;
      const newData = {
        highSchoolName: data.highSchoolName,
        institutionId: data.institutionId,
        institutionTypeId: data.institutionTypeId,
        isInHighSchool:
          data.institutionId > 0 ? false : gap ? false : true,
        isTakingGapYear: gap,
        matriculationYear: data.matriculationYear,
        provinceId: data.provinceId,
        subjectIds: normalizeSubjectIds(data.subjectIds),
        qualificationStartYear: data.qualificationStartYear,
        yearOfStudyId: data.yearOfStudyId,
      } as InstitutionDto;
      setFormValues(newData);
      setIsPrefilledFromPreviousYear(!!data.isPrefilledFromPreviousYear);
    }
  };

  useEffect(() => {
    loadEducation();
  }, [applicant.loginState.accessToken]);

  const onChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    rec: DropdownDto
  ) => {
    const id = Number(rec.id);
    if (!Number.isFinite(id)) return;

    const current = normalizeSubjectIds(formik.values.subjectIds);
    let subjects: number[];
    if (event.target.checked) {
      subjects = current.includes(id) ? current : [...current, id];
    } else {
      subjects = current.filter((s) => s !== id);
    }

    formik.setFieldValue('subjectIds', subjects);
  };

  const isChecked = (data: DropdownDto) => {
    const id = Number(data.id);
    if (!Number.isFinite(id)) return false;
    return normalizeSubjectIds(formik.values.subjectIds).includes(id);
  };

  const disabledSelectNoIamStillInHighSchool = () => {
    const currentYear = new Date().getFullYear();
    const matriculationYear = parseInt(
      formik.values.matriculationYear.toString()
    );
    if (matriculationYear === currentYear) return true;
    else return false;
  };

  useEffect(() => {
    disabledSelectNoIamStillInHighSchool();
  }, [formik.values.matriculationYear]);

  const populateDropdownForQualificationStartYear = () => {
    if (qualificationList.length < 1) {
      let years = [] as number[];
      const currentYear = new Date().getFullYear();
      let start = currentYear;
      for (let i = 8; i >= 0; i--) {
        years.push(start);
        start--;
      }

      setQualificationList(years);
    }

    return (
      <>
        <FormControl fullWidth>
          <InputLabel
            id='qualificationStartYear'
            error={
              formik.touched.qualificationStartYear &&
              Boolean(formik.errors.qualificationStartYear)
            }>
            Select Qualification start year
          </InputLabel>
          <Select
            name='qualificationStartYear'
            labelId='qualificationStartYear'
            id='qualificationStartYear'
            value={formik.values.qualificationStartYear}
            label='Select Qualification start year'
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={
              formik.touched.qualificationStartYear &&
              Boolean(formik.errors.qualificationStartYear)
            }>
            <MenuItem value={0} disabled>
              Select Qualification start year
            </MenuItem>
            {qualificationList.map((item) => {
              return (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Box className='error_material'>
          {formik.touched.qualificationStartYear &&
            (formik.errors.qualificationStartYear as React.ReactNode)}
        </Box>
      </>
    );
  };
  return (
    <Grid container spacing={2}>
      <Dialog
        open={matricGapBlockOpen}
        onClose={() => setMatricGapBlockOpen(false)}
        fullWidth
        maxWidth='lg'
        aria-labelledby='matric-gap-dialog-title'
        PaperProps={{
          sx: {
            p: { xs: 1, sm: 2 },
            borderRadius: 2,
            maxWidth: { xs: 'calc(100vw - 16px)', sm: 720, md: 900 },
          },
        }}>
        <DialogTitle
          id='matric-gap-dialog-title'
          sx={{
            fontSize: { xs: '0.8125rem', sm: '1rem', md: '1.125rem' },
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: '-0.02em',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            px: { xs: 1, sm: 2 },
            pt: { xs: 2, sm: 2.5 },
            pb: 1,
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: 3,
              bgcolor: 'action.disabledBackground',
            },
          }}>
          {
            'Please note that your matriculation year does not meet the Thuthuka Bursary criteria'
          }
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant='body1' paragraph sx={{ fontWeight: 600 }}>
            The bursary requires that you must not have more than 2 years
            between:
          </Typography>
          <Typography component='ul' variant='body1' sx={{ pl: 2, m: 0, mb: 2 }}>
            <li>Your matric year, and</li>
            <li>
              The year you start your first year (1st-year) university studies
            </li>
          </Typography>
          <Typography variant='body1' paragraph>
            For the {nextBursaryIntakeCalendarYear()} first year intake, this
            means:
          </Typography>
          <Typography variant='body1' sx={{ mb: 1 }}>
            You should have matriculated in {nextBursaryIntakeCalendarYear() - 1}{' '}
            or {nextBursaryIntakeCalendarYear() - 2}.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant='contained'
            size='large'
            fullWidth
            onClick={() => setMatricGapBlockOpen(false)}>
            I understand
          </Button>
        </DialogActions>
      </Dialog>
      {isPrefilledFromPreviousYear && (
        <Grid item xs={12}>
          <Alert severity='info' variant='standard'>
            We've pre-filled your education details from your previous
            application. Please review and update anything that has changed,
            then click "Save and Continue" to confirm it for this year.
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        {isSaveAndCloseMessage && isSaveAndCloseMessage !== '' && (
          <SavedApplicationPopUp
            isSaveAndClose={isSaveAndClose}
            message={isSaveAndCloseMessage}
            status={applicant.applicantCreateState.status}
          />
        )}
        <h4>High School</h4>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          id='highSchoolName'
          name='highSchoolName'
          label='High school attending/attended'
          value={formik.values.highSchoolName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          type='text'
          error={
            formik.touched.highSchoolName &&
            Boolean(formik.errors.highSchoolName)
          }
          helperText={
            formik.touched.highSchoolName && formik.errors.highSchoolName
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          id='matriculationYear'
          name='matriculationYear'
          label='Matriculation Year'
          value={formik.values.matriculationYear}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          type='text'
          error={
            formik.touched.matriculationYear &&
            Boolean(formik.errors.matriculationYear)
          }
          helperText={
            formik.touched.matriculationYear && formik.errors.matriculationYear
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Provinces
          formik={formik}
          name='provinceId'
          value={formik.values.provinceId}
          error={
            formik.touched.provinceId &&
            Boolean(formik.errors.provinceId as React.ReactNode)
          }
          touched={formik.touched.provinceId}
        />
      </Grid>
      <Grid item xs={12}>
        <InputLabel htmlFor='my-input'>Select Grade 12 Subjects</InputLabel>
        <Box>
          {selData.constantState.subjects &&
            selData.constantState.subjects.map(
              (data: DropdownDto) => {
                return (
                  <FormControlLabel
                    key={data.id}
                    control={
                      <Checkbox
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                        onChange={(event) => onChange(event, data)}
                        checked={isChecked(data)}
                        name={data.description}
                      />
                    }
                    label={data.description.replace(/([A-Z])/g, ' $1').trim()}
                    value={data.id}
                  />
                );
              }
            )}
          <Box className='error_material'>
            {formik.touched.subjectIds &&
              (formik.errors.subjectIds as React.ReactNode)}
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <h4>Current Institution Details</h4>
      </Grid>

      <Grid item xs={12}>
        <FormControl>
          <FormLabel id='demo-controlled-radio-buttons-group'>
            Are you currently still in high school?
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby='demo-controlled-radio-buttons-group'
            name='isInHighSchool'
            value={
              formik.values.isInHighSchool === true
                ? 'true'
                : 'false'
            }
            onChange={(e) => {
              if (e.target.value === 'false')
                formik.setFieldValue('isInHighSchool', false);
              else if (e.target.value === 'true')
                formik.setFieldValue('isInHighSchool', true);
              formik.setFieldValue('institutionId', 0);
              formik.setFieldValue('qualificationStartYear', 0);
              formik.setFieldValue('yearOfStudyId', 0);
              formik.setFieldValue('isTakingGapYear', false);
            }}>
            <FormControlLabel
              disabled={disabledSelectNoIamStillInHighSchool()}
              value='false'
              control={<Radio />}
              label='No'
            />
            <FormControlLabel value='true' control={<Radio />} label='Yes' />
          </RadioGroup>
        </FormControl>
      </Grid>

      {!formik.values.isInHighSchool && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formik.values.isTakingGapYear === true}
                onChange={(_, checked) => {
                  formik.setFieldValue('isTakingGapYear', checked);
                  if (checked) {
                    formik.setFieldValue('institutionId', 0);
                    formik.setFieldValue('yearOfStudyId', 1);
                    void formik.setFieldValue(
                      'qualificationStartYear',
                      nextBursaryIntakeCalendarYear()
                    );
                  } else {
                    formik.setFieldValue('yearOfStudyId', 0);
                  }
                }}
              />
            }
            label='Taking a gap year (not yet enrolled at a university; applying for first year)'
          />
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            If you are already at a university, leave this unticked and select
            your institution below.
          </Typography>
        </Grid>
      )}

      {!formik.values.isInHighSchool && !formik.values.isTakingGapYear && (
        <Grid item xs={12} md={6}>
          <Universities formik={formik} />
        </Grid>
      )}

      {!formik.values.isInHighSchool && !formik.values.isTakingGapYear && (
        <Grid item xs={12} md={6}>
          {populateDropdownForQualificationStartYear()}
        </Grid>
      )}

      {!formik.values.isInHighSchool && formik.values.isTakingGapYear && (
        <Grid item xs={12}>
          <Alert severity='info' variant='standard'>
            You are applying for <strong>first year</strong> of tertiary study
            after your gap year. On the next steps you will choose universities
            for that first year.
          </Alert>
        </Grid>
      )}

      {!formik.values.isInHighSchool && !formik.values.isTakingGapYear && (
        <Grid item xs={12} md={6}>
          <YearOfStudy
            formik={formik}
            isDisabled={false}
            label='Current Year Of Study'
            removePGDA={true}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <StepperButtons
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          formik={formik}
          status={applicant.applicantCreateState.status}
          isDisabled={false}
          setSaveAndClose={setIsSaveAndClose}
        />
      </Grid>
    </Grid>
  );
}
